import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import type { SystemStyleObject } from "@mui/system";

import React from "react";

import { Theme, useTheme } from "@mui/material";

export type StyledButtonProps = Omit<LoadingButtonProps, "children"> & {
  /** the text value for the button */
  label: string | React.ReactNode;
  /** updates the style theme to match the grey button found in the design */
  isGreyStyle?: boolean;
  /** updates the style theme to match the grey button found in the design */
  isDeleteStyle?: boolean;
  /** Design sometimes require the button to be thinner than MUI design which is done by height fixing. */
  thin?: boolean;
  /** Design button based on new design system */
  isOscarDesign?: boolean;
};

/** This button is a styled MUI Button where all the Button props are passed through. There are two additional values:
 * <p> First, the button label which is the text for the button
 * <p> Second is `isGreyStyle` which updates the style theme to match the grey button found in the design*/
export const StyledButton = ({
  label,
  isGreyStyle = false,
  isDeleteStyle = false,
  thin = false,
  isOscarDesign,
  sx,
  ...props
}: StyledButtonProps) => {
  const { typography } = useTheme();
  const sxArray =
    sx === undefined
      ? []
      : sx instanceof Array
      ? sx
      : ([sx] as ReadonlyArray<
          | boolean
          | SystemStyleObject<Theme>
          | ((theme: Theme) => SystemStyleObject<Theme>)
        >);

  const ownSx = [thin ? { py: isOscarDesign ? 0 : 0.5 } : {}] as ReadonlyArray<
    SystemStyleObject<Theme>
  >;

  const greyButtonSx = () =>
    [
      ({ palette }) => ({
        color: palette.grey.A700,
        backgroundColor:
          props.variant === "text" ? palette.grey[50] : palette.grey.A200,
        border: "none",
        "&:hover": {
          backgroundColor: palette.grey.A100,
          border: "none",
        },
        "&:disabled": {
          border: "none",
        },
      }),
    ] as ReadonlyArray<(theme: Theme) => SystemStyleObject<Theme>>;

  const deleteButtonSx = () =>
    [
      ({ palette }) => ({
        color: palette.common.white,
        backgroundColor: palette.error.light,
        border: "none",
        "&:hover": {
          backgroundColor: palette.error.dark,
          border: "none",
        },
        "&:disabled": {
          border: "none",
        },
      }),
    ] as ReadonlyArray<(theme: Theme) => SystemStyleObject<Theme>>;

  return (
    <LoadingButton
      disableElevation={true}
      type="button"
      aria-label={isDeleteStyle ? "delete" : undefined}
      sx={[
        ...(isOscarDesign
          ? [typography.buttonL as SystemStyleObject<Theme>]
          : []),
        ...sxArray,
        ...ownSx,
        ...(isGreyStyle ? greyButtonSx() : []),
        ...(isDeleteStyle ? deleteButtonSx() : []),
      ]}
      {...props}
    >
      {label}
    </LoadingButton>
  );
};
